// ---------------------------------------------------------------------------
// DiCharts SDK – Toolbar
// ---------------------------------------------------------------------------

import { createTickAggregator } from '../../dicharts-lib/dist/index.js';
import type { DiChartInstance, TickAggregator } from '../../dicharts-lib/dist/index.js';
import type { CandleStyle, OverlayConfig, SubPaneConfig, ToolbarConfig } from './types';
import { CANDLE_STYLES, INDICATORS, TIMEFRAMES } from './types';
import type { TimeframeInfo } from './types';
import type { EventEmitter } from './events';
import {
  ICON_CANDLE, ICON_CHEVRON, ICON_INDICATORS, ICON_SETTINGS,
  ICON_MOON, ICON_SUN, ICON_SEARCH,
  ICON_FULLSCREEN, ICON_FULLSCREEN_EXIT,
  ICON_LAYOUT,
  STYLE_ICONS,
} from './ui/icons';

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html) e.innerHTML = html;
  return e;
}

function btn(cls: string, html: string, title?: string): HTMLButtonElement {
  const b = el('button', cls, html);
  b.type = 'button';
  if (title) b.title = title;
  return b;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ToolbarState {
  currentTheme: 'dark' | 'light';
  currentStyle: CandleStyle;
  currentTimeframeKey: string;
  volumeEnabled: boolean;
  activeIndicators: Set<string>;
  showBuyLine: boolean;
  showTPLine: boolean;
  showSLLine: boolean;
  aggregator: TickAggregator | null;
}

export interface ToolbarCallbacks {
  onStyleChange: (style: CandleStyle) => void;
  onTimeframeChange: (tf: TimeframeInfo) => void;
  onIndicatorToggle: () => void;
  onLayoutChange: (layout: string) => void;
  onVolumeToggle: (enabled: boolean) => void;
  onOrderLineChange: () => void;
  onThemeToggle: () => void;
  onFullscreen: (root: HTMLElement) => void;
}

// ---------------------------------------------------------------------------
// Build toolbar
// ---------------------------------------------------------------------------

export function buildToolbar(
  root: HTMLElement,
  tbConfig: ToolbarConfig,
  state: ToolbarState,
  callbacks: ToolbarCallbacks,
  emitter: EventEmitter,
  symbolName: string,
  streamingTimeframe: string | undefined,
): { toolbar: HTMLElement; priceEl: HTMLElement | null; popups: HTMLElement[] } {
  const toolbar = el('div', 'dw-toolbar');
  const tbLeft = el('div', 'dw-toolbar-left');
  const tbRight = el('div', 'dw-toolbar-right');

  const popups: HTMLElement[] = [];
  let priceEl: HTMLElement | null = null;

  function closeAllPopups() {
    for (const p of popups) p.classList.remove('dw-open');
  }

  function togglePopup(popup: HTMLElement, e: Event) {
    e.stopPropagation();
    const wasOpen = popup.classList.contains('dw-open');
    closeAllPopups();
    if (!wasOpen) popup.classList.add('dw-open');
  }

  // ── Symbol & Price ──
  if (symbolName) {
    const group = el('div', 'dw-symbol-group');
    const nameEl = el('span', 'dw-symbol-name', symbolName);
    group.appendChild(nameEl);

    priceEl = el('span', 'dw-symbol-price dw-up');
    group.appendChild(priceEl);

    tbLeft.appendChild(group);
    tbLeft.appendChild(el('div', 'dw-sep'));
  }

  // ── Style picker ──
  if (tbConfig.stylePicker !== false) {
    const wrap = el('div', 'dw-popup-wrap');
    const trigger = btn('dw-btn', `${ICON_CANDLE}${ICON_CHEVRON}`, 'Chart Style');

    const popup = el('div', 'dw-popup dw-left');
    popup.innerHTML = `<div class="dw-popup-title">Chart Style</div>`;
    const opts = el('div', 'dw-style-opts');

    for (const s of CANDLE_STYLES) {
      const opt = btn(
        `dw-style-opt${s.value === state.currentStyle ? ' dw-active' : ''}`,
        `${STYLE_ICONS[s.value] || ''}<span>${s.label}</span>`,
      );
      opt.dataset.style = s.value;
      opt.addEventListener('click', () => {
        state.currentStyle = s.value;
        callbacks.onStyleChange(s.value);
        emitter.emit('styleChange', s.value);
        opts.querySelectorAll('.dw-style-opt').forEach(b => b.classList.remove('dw-active'));
        opt.classList.add('dw-active');
        popup.classList.remove('dw-open');
      });
      opts.appendChild(opt);
    }

    popup.appendChild(opts);
    popups.push(popup);

    trigger.addEventListener('click', (e) => togglePopup(popup, e));
    popup.addEventListener('click', (e) => e.stopPropagation());

    wrap.appendChild(trigger);
    wrap.appendChild(popup);
    tbLeft.appendChild(wrap);
  }

  // ── Separator after style picker ──
  if (tbConfig.stylePicker !== false && (tbConfig.timeframes !== false || tbConfig.indicators !== false)) {
    tbLeft.appendChild(el('div', 'dw-sep'));
  }

  // ── Timeframe buttons ──
  if (tbConfig.timeframes !== false) {
    const tfGroup = el('div', 'dw-tf-group');
    const tfBtns: HTMLButtonElement[] = [];

    for (const tf of TIMEFRAMES) {
      const b = btn(`dw-tf-btn${tf.key === state.currentTimeframeKey ? ' dw-active' : ''}`, tf.label);
      b.dataset.tf = tf.key;
      b.addEventListener('click', () => {
        state.currentTimeframeKey = tf.key;
        tfBtns.forEach(x => x.classList.remove('dw-active'));
        b.classList.add('dw-active');
        if (streamingTimeframe) {
          state.aggregator = createTickAggregator(tf.intervalMs);
        }
        callbacks.onTimeframeChange(tf);
        emitter.emit('timeframeChange', tf);
      });
      tfBtns.push(b);
      tfGroup.appendChild(b);
    }

    tbLeft.appendChild(tfGroup);
  }

  // ── Separator before indicators ──
  if (tbConfig.timeframes !== false && tbConfig.indicators !== false) {
    tbLeft.appendChild(el('div', 'dw-sep'));
  }

  // ── Indicators popup ──
  if (tbConfig.indicators !== false) {
    const wrap = el('div', 'dw-popup-wrap');
    const trigger = btn('dw-btn', `${ICON_INDICATORS}<span>Indicators</span>${ICON_CHEVRON}`, 'Indicators');

    const popup = el('div', 'dw-popup dw-left dw-ind-popup');

    // Header with search
    const header = el('div', 'dw-ind-header');
    const title = el('span', 'dw-popup-title', 'Indicators');
    title.style.marginBottom = '0';
    title.style.paddingBottom = '0';
    title.style.borderBottom = 'none';
    const searchWrap = el('div', 'dw-ind-search');
    searchWrap.innerHTML = ICON_SEARCH;
    const searchInput = el('input') as HTMLInputElement;
    searchInput.type = 'text';
    searchInput.placeholder = 'Search...';
    searchWrap.appendChild(searchInput);
    header.appendChild(title);
    header.appendChild(searchWrap);
    popup.appendChild(header);

    // Tabs
    const tabs = el('div', 'dw-ind-tabs');
    const tabAll = btn('dw-ind-tab dw-active', 'All');
    tabAll.dataset.cat = 'all';
    const tabOverlay = btn('dw-ind-tab', 'Overlays');
    tabOverlay.dataset.cat = 'overlay';
    const tabOsc = btn('dw-ind-tab', 'Oscillators');
    tabOsc.dataset.cat = 'oscillator';
    tabs.append(tabAll, tabOverlay, tabOsc);
    popup.appendChild(tabs);

    let currentCat = 'all';
    const tabBtns = [tabAll, tabOverlay, tabOsc];
    tabBtns.forEach(t => {
      t.addEventListener('click', () => {
        currentCat = t.dataset.cat!;
        tabBtns.forEach(b => b.classList.remove('dw-active'));
        t.classList.add('dw-active');
        filterRows();
      });
    });

    // Indicator rows
    const list = el('div', 'dw-ind-list');
    const rows: HTMLElement[] = [];

    for (const ind of INDICATORS) {
      const row = btn(
        `dw-ind-row${state.activeIndicators.has(ind.id) ? ' dw-active' : ''}`,
        '',
      );
      row.dataset.ind = ind.id;
      row.dataset.cat = ind.category;

      const dot = el('span', 'dw-ind-dot');
      dot.style.background = ind.color;
      const name = el('span', 'dw-ind-name');
      name.textContent = ind.name;
      if (ind.description) {
        const desc = el('span', 'dw-ind-desc');
        desc.textContent = ` ${ind.description}`;
        name.appendChild(desc);
      }
      const star = el('span', 'dw-ind-star', '&#9733;');

      row.append(dot, name, star);

      row.addEventListener('click', () => {
        if (state.activeIndicators.has(ind.id)) {
          state.activeIndicators.delete(ind.id);
          row.classList.remove('dw-active');
          emitter.emit('indicatorRemove', ind.id);
        } else {
          state.activeIndicators.add(ind.id);
          row.classList.add('dw-active');
          emitter.emit('indicatorAdd', ind.id);
        }
        callbacks.onIndicatorToggle();
      });

      rows.push(row);
      list.appendChild(row);
    }

    popup.appendChild(list);
    popups.push(popup);

    function filterRows() {
      const q = searchInput.value.toLowerCase().trim();
      for (const row of rows) {
        const matchCat = currentCat === 'all' || row.dataset.cat === currentCat;
        const text = row.textContent!.toLowerCase();
        const matchSearch = !q || text.includes(q) || row.dataset.ind!.includes(q);
        row.classList.toggle('dw-hidden', !(matchCat && matchSearch));
      }
    }

    searchInput.addEventListener('input', filterRows);

    trigger.addEventListener('click', (e) => {
      togglePopup(popup, e);
      if (popup.classList.contains('dw-open')) {
        searchInput.value = '';
        filterRows();
        for (const row of rows) {
          row.classList.toggle('dw-active', state.activeIndicators.has(row.dataset.ind!));
        }
      }
    });
    popup.addEventListener('click', (e) => e.stopPropagation());

    wrap.appendChild(trigger);
    wrap.appendChild(popup);
    tbLeft.appendChild(wrap);
  }

  // ── Layout picker ──
  if (tbConfig.layout !== false) {
    const LAYOUT_OPTIONS: { id: string; label: string; iconClass: string }[] = [
      { id: 'single', label: 'Single', iconClass: '' },
      { id: '2v', label: 'Two Vertical', iconClass: 'dw-lo-2v' },
      { id: '2h', label: 'Two Horizontal', iconClass: 'dw-lo-2h' },
      { id: '1-2r', label: '1 + 2 Right', iconClass: 'dw-lo-1-2r' },
      { id: '4grid', label: 'Quad Grid', iconClass: 'dw-lo-4' },
    ];

    const wrap = el('div', 'dw-popup-wrap');
    const trigger = btn('dw-btn', `${ICON_LAYOUT}${ICON_CHEVRON}`, 'Chart Layout');

    const popup = el('div', 'dw-popup dw-right');
    popup.innerHTML = `<div class="dw-popup-title">Chart Layout</div>`;
    const opts = el('div', 'dw-layout-opts');

    for (const lo of LAYOUT_OPTIONS) {
      const opt = btn(`dw-layout-opt${lo.id === 'single' ? ' dw-active' : ''}`, '');
      opt.dataset.layout = lo.id;

      const icon = el('div', `dw-lo-icon${lo.iconClass ? ' ' + lo.iconClass : ''}`);
      if (lo.id === 'single') {
        const cell = el('div', 'dw-lo-cell');
        cell.style.cssText = 'grid-column:1/3;grid-row:1/3';
        icon.appendChild(cell);
      } else if (lo.id === '1-2r') {
        const c1 = el('div', 'dw-lo-cell');
        c1.style.gridRow = '1/3';
        icon.append(c1, el('div', 'dw-lo-cell'), el('div', 'dw-lo-cell'));
      } else {
        const count = lo.id === '4grid' ? 4 : 2;
        for (let i = 0; i < count; i++) icon.appendChild(el('div', 'dw-lo-cell'));
      }

      const label = el('span', undefined, lo.label);
      opt.append(icon, label);

      opt.addEventListener('click', () => {
        popup.classList.remove('dw-open');
        callbacks.onLayoutChange(lo.id);
        emitter.emit('layoutChange', lo.id);
        opts.querySelectorAll('.dw-layout-opt').forEach(b => b.classList.remove('dw-active'));
        opt.classList.add('dw-active');
      });

      opts.appendChild(opt);
    }

    popup.appendChild(opts);
    popups.push(popup);

    trigger.addEventListener('click', (e) => togglePopup(popup, e));
    popup.addEventListener('click', (e) => e.stopPropagation());

    wrap.appendChild(trigger);
    wrap.appendChild(popup);
    tbRight.appendChild(wrap);
  }

  // ── Settings popup ──
  if (tbConfig.settings !== false) {
    const wrap = el('div', 'dw-popup-wrap');
    const trigger = btn('dw-btn', `${ICON_SETTINGS}${ICON_CHEVRON}`, 'Settings');

    const popup = el('div', 'dw-popup dw-right dw-settings');
    popup.innerHTML = `<div class="dw-popup-title">Settings</div>`;

    // Volume checkbox
    const volSection = el('div', 'dw-settings-section');
    const volLabel = el('label', 'dw-check');
    const volCb = el('input') as HTMLInputElement;
    volCb.type = 'checkbox';
    volCb.checked = state.volumeEnabled;
    volLabel.appendChild(volCb);
    volLabel.appendChild(document.createTextNode(' Volume Bars'));
    volSection.appendChild(volLabel);
    popup.appendChild(volSection);

    volCb.addEventListener('change', () => {
      state.volumeEnabled = volCb.checked;
      callbacks.onVolumeToggle(volCb.checked);
    });

    // ── Order lines section ──
    const olSection = el('div', 'dw-settings-section');
    const olTitle = el('div', 'dw-settings-label', 'Order Lines');
    olSection.appendChild(olTitle);

    const orderLineItems: { label: string; color: string; key: keyof Pick<ToolbarState, 'showBuyLine' | 'showTPLine' | 'showSLLine'> }[] = [
      { label: 'Buy Limit', color: '#26a69a', key: 'showBuyLine' },
      { label: 'Take Profit', color: '#FFD700', key: 'showTPLine' },
      { label: 'Stop Loss', color: '#ef5350', key: 'showSLLine' },
    ];

    for (const item of orderLineItems) {
      const lbl = el('label', 'dw-check');
      const cb = el('input') as HTMLInputElement;
      cb.type = 'checkbox';
      cb.checked = state[item.key];
      lbl.appendChild(cb);
      lbl.appendChild(document.createTextNode(` ${item.label}`));
      const dot = el('span', 'dw-color-dot');
      dot.style.background = item.color;
      lbl.appendChild(dot);
      cb.addEventListener('change', () => {
        state[item.key] = cb.checked;
        callbacks.onOrderLineChange();
      });
      olSection.appendChild(lbl);
    }
    popup.appendChild(olSection);

    popups.push(popup);
    trigger.addEventListener('click', (e) => togglePopup(popup, e));
    popup.addEventListener('click', (e) => e.stopPropagation());

    wrap.appendChild(trigger);
    wrap.appendChild(popup);
    tbRight.appendChild(wrap);
  }

  // ── Fullscreen toggle ──
  if (tbConfig.fullscreen !== false) {
    const fsBtn = btn('dw-btn dw-btn-icon', ICON_FULLSCREEN, 'Fullscreen');

    fsBtn.addEventListener('click', () => {
      callbacks.onFullscreen(root);
    });

    document.addEventListener('fullscreenchange', () => {
      const isFull = document.fullscreenElement === root;
      fsBtn.innerHTML = isFull ? ICON_FULLSCREEN_EXIT : ICON_FULLSCREEN;
    });

    tbRight.appendChild(fsBtn);
  }

  // ── Theme toggle ──
  if (tbConfig.themeToggle !== false) {
    const themeBtn = btn('dw-btn dw-btn-icon', state.currentTheme === 'dark' ? ICON_MOON : ICON_SUN, 'Toggle theme');

    themeBtn.addEventListener('click', () => {
      if (state.currentTheme === 'dark') {
        state.currentTheme = 'light';
        root.classList.add('dw-light');
        themeBtn.innerHTML = ICON_SUN;
      } else {
        state.currentTheme = 'dark';
        root.classList.remove('dw-light');
        themeBtn.innerHTML = ICON_MOON;
      }
      callbacks.onThemeToggle();
      emitter.emit('themeChange', state.currentTheme);
    });

    tbRight.appendChild(themeBtn);
  }

  toolbar.appendChild(tbLeft);
  toolbar.appendChild(tbRight);

  // Close popups on root click
  root.addEventListener('click', closeAllPopups);

  return { toolbar, priceEl, popups };
}
